/**
 * Marketing sayfaları (navbar, auth, iletişim, görüşme, hakkımızda) — landing ile aynı açık tema.
 * @see components/marketing/landing/brandTokens.js
 */
import {
  BRAND,
  MARKETING_CONTAINER_SX,
  MARKETING_CONTENT_MAX_WIDTH,
} from "../components/marketing/landing/brandTokens";

export const FONT = "'Poppins', sans-serif";

export const M = {
  primary: BRAND.teal,
  primaryDark: BRAND.tealDark,
  primaryHover: BRAND.tealDark,
  primaryDim: BRAND.tealLight,
  white: BRAND.pageBg,
  dark: BRAND.navy,
  navy: BRAND.navy,
  body: BRAND.textSecondary,
  muted: BRAND.textMuted,
  textMuted: BRAND.textMuted,
  lightText: BRAND.textMuted,
  lightBg: BRAND.sectionBgMuted,
  borderLight: BRAND.borderLight,
  borderDark: BRAND.borderOnDark,
  cardDarkGradient:
    "linear-gradient(165deg, #0a1628 0%, #0e1f3d 45%, #132842 100%)",
};

export const ERROR_RED = "#ef4444";

/** MarketingNavbar (glass AppBar) yaklaşık yükseklik — içerik mt/pt için */
export const MARKETING_NAV_HEIGHT = 104;

/** Landing StatsSection ile aynı içerik şeridi */
export const CONTAINER = MARKETING_CONTAINER_SX;

const fontBase = { fontFamily: FONT };

export const TYPE = {
  body: { ...fontBase, fontSize: "1rem", lineHeight: 1.55, color: M.body },
  caption: { ...fontBase, fontSize: "0.8125rem", lineHeight: 1.45, color: M.muted },
  sectionH2: {
    ...fontBase,
    fontSize: { xs: "1.75rem", md: "2.25rem" },
    fontWeight: 800,
    lineHeight: 1.15,
    color: M.navy,
  },
  sectionH2OnDark: {
    ...fontBase,
    fontSize: { xs: "1.75rem", md: "2.25rem" },
    fontWeight: 800,
    lineHeight: 1.15,
    color: BRAND.textOnDark,
  },
  cardH3: {
    ...fontBase,
    fontSize: "1.125rem",
    fontWeight: 700,
    lineHeight: 1.3,
    color: M.navy,
  },
};

/** Tüm marketing alt sayfaları — ana sayfa ile aynı zemin */
export const marketingPageShellSx = {
  width: "100%",
  minHeight: "100vh",
  bgcolor: BRAND.pageBg,
  color: BRAND.navy,
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  "& h1, & h2, & h3": {
    fontFamily: "'Inter', 'Poppins', sans-serif !important",
  },
};

/** Hero / üst bant (iletişim, hakkımızda) */
export const marketingHeroSectionSx = {
  background: BRAND.gradientHero,
  pt: `${MARKETING_NAV_HEIGHT + 64}px`, // Increased top padding
  pb: { xs: 6, md: 8 },
};

/** Auth / görüşme sol panel — açık teal tint (koyu gradient yerine) */
export const marketingSplitPanelSx = {
  background: BRAND.gradientSectionMuted,
  borderRight: { md: `1px solid ${BRAND.borderLight}` },
  color: BRAND.navy,
  position: "relative",
  overflow: "hidden",
};

/** Split panel sol içerik — landing MARKETING_CONTAINER_SX sol kenarı ile hizalı */
export const marketingSplitContentInsetSx = {
  width: "100%",
  maxWidth: { xs: 440, md: 480, lg: 520 },
  boxSizing: "border-box",
  pl: {
    xs: 0,
    md: `calc((100vw - min(100vw, ${MARKETING_CONTENT_MAX_WIDTH}px)) / 2 + 36px)`,
    lg: `calc((100vw - min(100vw, ${MARKETING_CONTENT_MAX_WIDTH}px)) / 2 + 44px)`,
    xl: `calc((100vw - min(100vw, ${MARKETING_CONTENT_MAX_WIDTH}px)) / 2 + 56px)`,
  },
  pr: { xs: 0, md: 3, lg: 4 },
};

/** Görüşme (/pricing) sol panel — daha geniş metin alanı */
export const pricingSplitContentSx = {
  ...marketingSplitContentInsetSx,
  maxWidth: { xs: "100%", md: "100%", lg: "100%" },
  pr: { xs: 0, md: 4, lg: 5, xl: 6 },
};

/** @deprecated Koyu panel — marketingSplitPanelSx kullanın */
export const marketingDarkBg = marketingSplitPanelSx;

export const marketingNavBarSx = {
  backgroundColor: "rgba(255, 255, 255, 0.88) !important",
  backdropFilter: "blur(20px) saturate(160%)",
  WebkitBackdropFilter: "blur(20px) saturate(160%)",
  borderBottom: `1px solid ${BRAND.borderLight}`,
  boxShadow: "0 4px 24px rgba(10, 22, 40, 0.08)",
  color: BRAND.navy,
};

export const marketingAuthCardSx = {
  p: { xs: 2.5, sm: 4, md: 5 },
  borderRadius: { xs: "16px", sm: "24px" },
  border: `1px solid rgba(0, 0, 0, 0.05)`,
  bgcolor: M.white,
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.04)",
};

export const marketingAuthGradientSx = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mt: { xs: "82px", md: `${MARKETING_NAV_HEIGHT}px` },
  minHeight: { xs: `calc(100vh - 82px)`, md: `calc(100vh - ${MARKETING_NAV_HEIGHT}px)` },
  width: "100%",
  background: "linear-gradient(135deg, #F1F5F9 0%, rgba(76, 206, 172, 0.25) 100%)",
  px: { xs: 2, sm: 3 },
  py: { xs: 3, sm: 4, md: 6 },
};

export const marketingBtnPrimarySx = {
  fontFamily: FONT,
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "12px",
  bgcolor: M.primary,
  color: BRAND.textOnDark,
  boxShadow: `0 8px 24px ${BRAND.tealGlow}`,
  "&:hover": { bgcolor: M.primaryDark },
  "&.Mui-disabled": {
    bgcolor: "rgba(76, 206, 172, 0.7)",
    color: "#ffffff",
    boxShadow: "none",
  },
};

export const marketingBtnSecondaryLightSx = {
  fontFamily: FONT,
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "12px",
  borderColor: M.borderLight,
  color: M.navy,
  "&:hover": { borderColor: M.primary, bgcolor: BRAND.tealLight },
};

export const marketingOutlinedInputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: FONT,
    borderRadius: "12px",
    bgcolor: BRAND.pageBg,
    color: M.navy,
    "& fieldset": { borderColor: M.borderLight },
    "&:hover fieldset": { borderColor: M.primary },
    "&.Mui-focused fieldset": { borderColor: M.primary },
  },
  "& .MuiInputLabel-root": { color: M.muted },
  "& .MuiInputLabel-root.Mui-focused": { color: M.primary },
};

export const marketingOutlinedInputDenseSx = {
  ...marketingOutlinedInputSx,
  "& .MuiOutlinedInput-input": { py: 1.1 },
};

export const marketingSelectMenuPaperSx = {
  borderRadius: "12px",
  border: `1px solid ${M.borderLight}`,
  boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
};

export const marketingSelectMenuItemSx = {
  fontFamily: FONT,
  fontSize: "0.9rem",
};

export const marketingAppCtaCardSurfaceSx = {
  borderRadius: { xs: "22px", md: "28px" },
  overflow: "hidden",
  background: BRAND.gradientTealCta,
  border: `1px solid ${BRAND.borderOnDark}`,
  boxShadow: `0 24px 64px ${BRAND.tealGlow}`,
};

export const BIRUNI_TEKNOPARK_MAP_EMBED =
  "https://maps.google.com/maps?q=Biruni%20%C3%9Cniversitesi%20Teknopark%C4%B1,%20Zeytinburnu,%20Istanbul&t=&z=15&ie=UTF8&iwloc=&output=embed";
